/* Security Center — admin için güvenlik denetim ve yedek paneli */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, CheckCircle2, XCircle, Download,
  Lock, KeyRound, Network, Wallet, Database, RefreshCw, AlertCircle,
  ListChecks, FileText, Terminal, Plus, Trash2, Hand, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { http, API, errMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const securityApi = {
  audit: () => http.get("/security/audit").then((r) => r.data),
  backupNow: () => http.post("/security/backup-now").then((r) => r.data),
  backups: () => http.get("/security/backups").then((r) => r.data),
  sendSecretsToTelegram: () =>
    http.post("/security/send-secrets-to-telegram").then((r) => r.data),
};

const checklistApi = {
  list: () => http.get("/checklist").then((r) => r.data),
  toggle: (id, done) =>
    http.patch(`/checklist/${id}`, { done }).then((r) => r.data),
  add: (b) => http.post("/checklist", b).then((r) => r.data),
  remove: (id) => http.delete(`/checklist/${id}`).then((r) => r.data),
  saveNotes: (content) =>
    http.put("/checklist/notes", { content }).then((r) => r.data),
};

const CATEGORY_META = {
  encryption: { label: "Şifreleme",  Icon: Lock,       color: "#2bd98a" },
  auth:       { label: "Kimlik",     Icon: KeyRound,   color: "#0A84FF" },
  network:    { label: "Ağ",         Icon: Network,    color: "#f0b90b" },
  trading:    { label: "Trading",    Icon: Wallet,     color: "#ff5d15" },
  backup:     { label: "Yedek",      Icon: Database,   color: "#a78bfa" },
  vault:      { label: "Kasa",       Icon: ShieldCheck,color: "#22d3ee" },
};

function fmtSize(bytes) {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function ScoreRing({ pct }) {
  const r = 40;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  const color = pct >= 90 ? "#2bd98a" : pct >= 70 ? "#f0b90b" : "#ff5662";
  return (
    <svg width="100" height="100" viewBox="0 0 100 100" data-testid="security-score-ring">
      <circle cx="50" cy="50" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
      <circle
        cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="8"
        strokeDasharray={`${dash} ${c}`}
        strokeLinecap="round"
        transform="rotate(-90 50 50)"
        style={{ transition: "stroke-dasharray 0.6s ease, stroke 0.4s ease" }}
      />
      <text x="50" y="50" textAnchor="middle" dominantBaseline="central"
            fill={color} fontSize="22" fontWeight="700" fontFamily="JetBrains Mono">
        {pct.toFixed(0)}
      </text>
      <text x="50" y="68" textAnchor="middle" fill="#83838d" fontSize="9"
            fontFamily="JetBrains Mono" letterSpacing="2">SCORE</text>
    </svg>
  );
}

function CheckRow({ name, check }) {
  const ok = check.ok;
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <div className="flex items-start gap-2 py-2 border-b border-white/5 last:border-0">
      <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${ok ? "text-[#2bd98a]" : "text-[#ff5662]"}`} />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-300">
          {name.replace(/_/g, " ")}
        </div>
        <div className={`text-xs mt-0.5 ${ok ? "text-zinc-400" : "text-[#ff5662]"}`}>
          {check.message}
        </div>
      </div>
    </div>
  );
}

function CategoryCard({ catKey, checks }) {
  const meta = CATEGORY_META[catKey] || { label: catKey, Icon: ShieldCheck, color: "#a1a1aa" };
  const Icon = meta.Icon;
  const total = Object.keys(checks).length;
  const passed = Object.values(checks).filter((c) => c.ok).length;
  return (
    <div
      className="rounded-lg border border-white/10 bg-[#0a0a0c]/60 p-4"
      data-testid={`security-cat-${catKey}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md flex items-center justify-center"
               style={{ background: `${meta.color}1a`, border: `1px solid ${meta.color}44` }}>
            <Icon className="w-4 h-4" style={{ color: meta.color }} />
          </div>
          <h3 className="text-sm font-semibold uppercase tracking-wider">{meta.label}</h3>
        </div>
        <span
          className="text-[10px] font-mono px-2 py-0.5 rounded-full"
          style={{
            background: passed === total ? "#2bd98a14" : "#ff566214",
            color: passed === total ? "#2bd98a" : "#ff5662",
            border: `1px solid ${passed === total ? "#2bd98a44" : "#ff566244"}`,
          }}
        >
          {passed}/{total}
        </span>
      </div>
      <div className="space-y-0">
        {Object.entries(checks).map(([k, c]) => (
          <CheckRow key={k} name={k} check={c} />
        ))}
      </div>
    </div>
  );
}

function BackupPanel() {
  const qc = useQueryClient();
  const { data: backups } = useQuery({
    queryKey: ["security-backups"],
    queryFn: securityApi.backups,
    refetchInterval: 30000,
  });
  const runMut = useMutation({
    mutationFn: securityApi.backupNow,
    onSuccess: (data) => {
      toast.success(`Yedek alındı (${fmtSize(data.size)})`);
      qc.invalidateQueries({ queryKey: ["security-backups"] });
      qc.invalidateQueries({ queryKey: ["security-audit"] });
    },
    onError: (e) => toast.error(errMsg(e, "Yedek başarısız")),
  });

  return (
    <div
      className="rounded-lg border border-white/10 bg-[#0a0a0c]/60 p-4"
      data-testid="security-backup-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-[#a78bfa]" />
          <h3 className="text-sm font-semibold uppercase tracking-wider">
            Yedek Yönetimi
          </h3>
        </div>
        <Button
          onClick={() => runMut.mutate()}
          disabled={runMut.isPending}
          size="sm"
          className="bg-[#a78bfa] hover:bg-[#a78bfa]/90 text-black font-semibold"
          data-testid="security-backup-now"
        >
          <Download className="w-3.5 h-3.5 mr-1.5" />
          {runMut.isPending ? "Alınıyor…" : "Şimdi Yedek Al"}
        </Button>
      </div>

      <p className="text-xs text-zinc-500 mb-3 font-mono leading-relaxed">
        Her gece UTC 03:00&apos;de otomatik · Fernet AES-128 şifreli ·
        Son 7 gün lokal · BACKUP_WEBHOOK_URL ile offsite&apos;e yüklenebilir
      </p>

      <div className="space-y-1">
        {(backups?.files || []).slice(0, 7).map((f) => (
          <div
            key={f.filename}
            className="flex items-center justify-between text-[11px] font-mono py-1.5 px-2 rounded hover:bg-white/5"
            data-testid={`backup-row-${f.filename}`}
          >
            <span className="text-zinc-300 truncate">{f.filename}</span>
            <span className="text-zinc-500 flex-shrink-0 ml-2">
              {fmtSize(f.size)} ·{" "}
              {new Date(f.modified_at).toLocaleString("tr-TR")}
            </span>
          </div>
        ))}
        {(!backups?.files || backups.files.length === 0) && (
          <div className="text-xs text-zinc-500 text-center py-4">
            Henüz yedek alınmamış. Yukarıdaki butona bas.
          </div>
        )}
      </div>
    </div>
  );
}

/* =========================================================================
   CHECKLIST + NOTES — Tek yerden tüm aksiyon takibi
   ========================================================================= */

function TelegramSecretsPanel() {
  const sendMut = useMutation({
    mutationFn: securityApi.sendSecretsToTelegram,
    onSuccess: (data) =>
      toast.success(`📲 Telegram'a gönderildi (${data.filename})`),
    onError: (e) =>
      toast.error(errMsg(e, "Gönderim başarısız")),
  });

  return (
    <div
      className="rounded-lg border border-[#22d3ee]/30 bg-[#22d3ee]/[0.04] p-4"
      data-testid="telegram-secrets-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <Send className="w-4 h-4 text-[#22d3ee]" />
            <h3 className="text-sm font-semibold uppercase tracking-wider">
              Telegram&apos;a Secret Backup Gönder
            </h3>
          </div>
          <p className="text-xs text-zinc-400 font-mono leading-relaxed">
            Tüm kritik secret&apos;lar (JWT_SECRET, FERNET_KEY, EMERGENT_LLM_KEY,
            proxy URL&apos;leri) bir <b>.txt dosyası</b> olarak senin Telegram
            chat&apos;ine gönderilir. Sunucu çökerse / .env silinirse hızlı
            kurtarma için.
          </p>
          <p className="text-[11px] text-[#f0b90b] mt-2 font-mono">
            ⚠️ Okuduktan SONRA Telegram&apos;dan SİL · Saatte max 3 kez
          </p>
        </div>
        <Button
          onClick={() => {
            if (
              window.confirm(
                "Tüm sunucu secret'larını Telegram chat'ine .txt olarak göndereceğim.\n\n" +
                  "⚠️ Bu çok hassas bilgi. Hesap hack'lenirse hepsi gider.\n" +
                  "Dosya geldikten sonra ASLA başkasıyla paylaşma, okuyup sil.\n\n" +
                  "Devam edilsin mi?",
              )
            ) {
              sendMut.mutate();
            }
          }}
          disabled={sendMut.isPending}
          className="bg-[#22d3ee] hover:bg-[#22d3ee]/90 text-black font-semibold flex-shrink-0"
          data-testid="security-send-secrets-btn"
        >
          <Send className="w-3.5 h-3.5 mr-1.5" />
          {sendMut.isPending ? "Gönderiliyor…" : "Telegram'a Gönder"}
        </Button>
      </div>
    </div>
  );
}

/* =========================================================================
   CHECKLIST
   ========================================================================= */

const CAT_STYLE = {
  P0: { color: "#ff5662", bg: "#ff566214", border: "#ff566244", label: "P0" },
  P1: { color: "#f0b90b", bg: "#f0b90b14", border: "#f0b90b44", label: "P1" },
  P2: { color: "#0A84FF", bg: "#0A84FF14", border: "#0A84FF44", label: "P2" },
};

function downloadRotationScript() {
  // Auth gerekli — token'ı header'a ekleyerek fetch
  const token = localStorage.getItem("darkex_jwt");
  if (!token) {
    toast.error("Önce giriş yap");
    return;
  }
  fetch(`${API}/checklist/rotation-script`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then(async (r) => {
      if (!r.ok) throw new Error(await r.text());
      return r.text();
    })
    .then((text) => {
      const blob = new Blob([text], { type: "text/x-shellscript" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "terminal-ex-rotate.sh";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Script indirildi — VPS'e kopyala ve `bash terminal-ex-rotate.sh` ile çalıştır");
    })
    .catch((e) => toast.error(`Script indirilemedi: ${e.message}`));
}

function ChecklistPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["checklist"],
    queryFn: checklistApi.list,
    refetchInterval: 60000,
  });
  const [expanded, setExpanded] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");

  const toggleMut = useMutation({
    mutationFn: ({ id, done }) => checklistApi.toggle(id, done),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["checklist"] }),
  });
  const addMut = useMutation({
    mutationFn: checklistApi.add,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["checklist"] });
      setAdding(false);
      setNewTitle("");
      setNewDesc("");
    },
  });
  const delMut = useMutation({
    mutationFn: checklistApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["checklist"] }),
  });

  const items = data?.items || [];
  const total = items.length;
  const done = items.filter((it) => it.done).length;

  return (
    <div
      className="rounded-lg border border-white/10 bg-[#0a0a0c]/60 p-4"
      data-testid="checklist-panel"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-[#ff5d15]" />
          <h3 className="text-sm font-semibold uppercase tracking-wider">
            Operasyon Checklist
          </h3>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-white/5 text-zinc-400">
            {done}/{total}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={downloadRotationScript}
            size="sm"
            variant="outline"
            className="border-[#ff5662]/40 text-[#ff5662] hover:bg-[#ff5662]/10"
            data-testid="checklist-download-script"
          >
            <Terminal className="w-3.5 h-3.5 mr-1.5" />
            Rotation Script İndir
          </Button>
          <Button
            onClick={() => setAdding(!adding)}
            size="sm"
            variant="ghost"
            className="text-zinc-400 hover:text-zinc-100"
            data-testid="checklist-add-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Yeni
          </Button>
        </div>
      </div>

      {adding && (
        <div className="mb-3 p-3 rounded-md border border-[#ff5d15]/30 bg-[#ff5d15]/[0.04] space-y-2">
          <input
            type="text"
            placeholder="Görev başlığı"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            className="w-full h-8 px-2 rounded bg-[#0a0a0c] border border-white/10 text-xs font-mono"
            data-testid="checklist-add-title"
          />
          <textarea
            placeholder="Açıklama (opsiyonel)"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            className="w-full px-2 py-1 rounded bg-[#0a0a0c] border border-white/10 text-xs font-mono resize-y min-h-[50px]"
            data-testid="checklist-add-desc"
          />
          <div className="flex justify-end gap-2">
            <Button
              size="sm" variant="ghost"
              onClick={() => { setAdding(false); setNewTitle(""); setNewDesc(""); }}
            >İptal</Button>
            <Button
              size="sm"
              disabled={!newTitle.trim() || addMut.isPending}
              onClick={() => addMut.mutate({
                title: newTitle.trim(),
                description: newDesc.trim(),
                category: "P2",
              })}
              className="bg-[#ff5d15] hover:bg-[#ff5d15]/90 text-black font-semibold"
              data-testid="checklist-add-save"
            >Ekle</Button>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        {isLoading && (
          <div className="text-xs text-zinc-500 py-3 text-center font-mono">
            Yükleniyor…
          </div>
        )}
        {items.map((it) => {
          const cs = CAT_STYLE[it.category] || CAT_STYLE.P2;
          const isOpen = expanded === it.id;
          return (
            <div
              key={it.id}
              className={`rounded-md border transition-colors ${
                it.done
                  ? "border-white/5 bg-white/[0.02] opacity-60"
                  : "border-white/10 bg-[#0a0a0c]/40 hover:border-white/20"
              }`}
              data-testid={`checklist-item-${it.key}`}
            >
              <div className="flex items-start gap-2 p-2.5">
                <button
                  onClick={() => toggleMut.mutate({ id: it.id, done: !it.done })}
                  className="mt-0.5 flex-shrink-0"
                  data-testid={`checklist-toggle-${it.key}`}
                >
                  {it.done ? (
                    <CheckCircle2 className="w-5 h-5 text-[#2bd98a]" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-zinc-600 hover:border-[#ff5d15]" />
                  )}
                </button>
                <div
                  className="flex-1 min-w-0 cursor-pointer"
                  onClick={() => setExpanded(isOpen ? null : it.id)}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded font-mono font-bold"
                      style={{ background: cs.bg, color: cs.color, border: `1px solid ${cs.border}` }}
                    >
                      {cs.label}
                    </span>
                    {it.manual_only && (
                      <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/5 text-zinc-500 font-mono inline-flex items-center gap-1">
                        <Hand className="w-2.5 h-2.5" /> MANUEL
                      </span>
                    )}
                    <span className={`text-sm ${it.done ? "line-through text-zinc-500" : "text-zinc-200"}`}>
                      {it.title}
                    </span>
                  </div>
                  {isOpen && it.description && (
                    <p className="text-[11px] text-zinc-400 mt-1.5 leading-relaxed whitespace-pre-wrap">
                      {it.description}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    if (window.confirm(`"${it.title}" görevini silmek istediğine emin misin?`)) {
                      delMut.mutate(it.id);
                    }
                  }}
                  className="opacity-0 group-hover:opacity-100 hover:opacity-100 text-zinc-500 hover:text-[#ff5662] p-1 flex-shrink-0"
                  data-testid={`checklist-delete-${it.key}`}
                  title="Sil"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
        {!isLoading && items.length === 0 && (
          <div className="text-xs text-zinc-500 py-4 text-center font-mono">
            Hiç görev yok.
          </div>
        )}
      </div>
    </div>
  );
}

function NotesPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["checklist-notes"],
    queryFn: checklistApi.list,
  });
  const [text, setText] = useState("");
  const [dirty, setDirty] = useState(false);
  const lastSaved = useRef("");
  const timer = useRef(null);

  useEffect(() => {
    if (data?.notes != null && lastSaved.current === "") {
      setText(data.notes);
      lastSaved.current = data.notes;
    }
  }, [data]);

  const saveMut = useMutation({
    mutationFn: checklistApi.saveNotes,
    onSuccess: () => {
      lastSaved.current = text;
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["checklist-notes"] });
    },
  });

  // Debounced autosave (2s)
  useEffect(() => {
    if (!dirty) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      saveMut.mutate(text);
    }, 2000);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, dirty]);

  return (
    <div
      className="rounded-lg border border-white/10 bg-[#0a0a0c]/60 p-4"
      data-testid="notes-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#22d3ee]" />
          <h3 className="text-sm font-semibold uppercase tracking-wider">
            Not Defteri
          </h3>
        </div>
        <span className="text-[10px] font-mono text-zinc-500">
          {saveMut.isPending ? "Kaydediliyor…" :
           dirty ? "● Kaydedilmemiş" :
           data?.notes_updated_at ? `Kaydedildi ${new Date(data.notes_updated_at).toLocaleTimeString("tr-TR")}` :
           "Otomatik kaydeder"}
        </span>
      </div>
      <textarea
        value={text}
        onChange={(e) => { setText(e.target.value); setDirty(true); }}
        placeholder="Hatırlatmalarını, parolaları (ŞIFRELI DEĞIL — Kasa'yı kullan!), URL'leri, taktik notlarını buraya yaz. Her şey DB'de kaydedilir, 2 sn sonra otomatik kaydedilir."
        className="w-full h-48 px-3 py-2 rounded bg-[#070708] border border-white/10 text-sm font-mono text-zinc-200 resize-y focus:border-[#22d3ee]/60 outline-none leading-relaxed"
        data-testid="notes-textarea"
      />
      <p className="text-[10px] text-zinc-600 mt-2 font-mono">
        ⚠️ Hassas bilgi (şifre, API key) için <b>Kasa</b>&apos;yı kullan — orası Fernet şifreli. Not defteri düz metin.
      </p>
    </div>
  );
}

export default function SecurityCenter() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const { data: audit, isLoading } = useQuery({
    queryKey: ["security-audit"],
    queryFn: securityApi.audit,
    refetchInterval: 30000,
    enabled: me?.role === "admin",
  });

  if (me?.role !== "admin") {
    return (
      <div className="p-8 max-w-md">
        <div className="bg-[#ff5662]/[0.08] border border-[#ff5662]/40 rounded-md p-4 flex items-start gap-2 text-sm">
          <AlertCircle className="w-4 h-4 text-[#ff5662] mt-0.5" />
          <div>
            <b className="text-[#ff5662]">Yetkin yok.</b>
            <p className="text-zinc-400 mt-1">
              Güvenlik merkezi sadece admin&apos;lere açık.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const score = audit?.score_pct ?? 0;
  const scoreColor =
    score >= 90 ? "#2bd98a" : score >= 70 ? "#f0b90b" : "#ff5662";

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="security-page">
      {/* ===== Hero ===== */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#ff5d15]/15 border border-[#ff5d15]/40 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-[#ff5d15]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                Güvenlik Merkezi
              </h1>
              <p className="text-xs text-zinc-500 font-mono mt-0.5">
                Tüm savunma katmanlarının canlı durumu · 30 sn&apos;de bir tazelenir
              </p>
            </div>
          </div>
        </div>
        <Button
          onClick={() => qc.invalidateQueries({ queryKey: ["security-audit"] })}
          variant="outline"
          className="border-white/10 text-zinc-300 hover:bg-white/5"
          data-testid="security-refresh"
        >
          <RefreshCw className="w-4 h-4 mr-1.5" />
          Yenile
        </Button>
      </div>

      {/* ===== Score banner ===== */}
      {isLoading && (
        <div className="text-center py-12 text-zinc-500 text-sm font-mono">
          Tarama yapılıyor…
        </div>
      )}
      {audit && (
        <div
          className="rounded-lg border p-5 flex items-center gap-5"
          style={{
            background: `linear-gradient(135deg, ${scoreColor}10, transparent)`,
            borderColor: `${scoreColor}44`,
          }}
          data-testid="security-score-banner"
        >
          <ScoreRing pct={score} />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">
              Genel Skor
            </div>
            <div className="text-3xl font-bold" style={{ color: scoreColor }}>
              {audit.passed}/{audit.total} kontrol başarılı
            </div>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
              {score >= 90 ? (
                <>
                  <ShieldCheck className="w-4 h-4 inline mr-1" />
                  Sistem canlı işleme hazır. Tüm kritik katmanlar aktif.
                </>
              ) : score >= 70 ? (
                <>
                  <ShieldAlert className="w-4 h-4 inline mr-1 text-[#f0b90b]" />
                  Birkaç uyarı var — kırmızı işaretli kontrolleri düzeltmen önerilir.
                </>
              ) : (
                <>
                  <ShieldAlert className="w-4 h-4 inline mr-1 text-[#ff5662]" />
                  KRİTİK: $$$ işlem açmadan önce kırmızı kontrolleri düzelt!
                </>
              )}
            </p>
          </div>
          <div className="text-right text-xs font-mono">
            <div className="text-zinc-500">Kullanıcı / Oturum</div>
            <div className="text-zinc-300 mt-1">
              {audit.users?.total ?? 0} kullanıcı · {audit.users?.active_sessions ?? 0} aktif oturum
            </div>
          </div>
        </div>
      )}

      {/* ===== Category grid ===== */}
      {audit && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(audit.checks || {}).map(([cat, checks]) => (
            <CategoryCard key={cat} catKey={cat} checks={checks} />
          ))}
        </div>
      )}

      {/* ===== Backup panel ===== */}
      <BackupPanel />

      {/* ===== Telegram Secret Backup ===== */}
      <TelegramSecretsPanel />

      {/* ===== Operasyon Checklist (DB persistent) ===== */}
      <ChecklistPanel />

      {/* ===== Not Defteri (autosave) ===== */}
      <NotesPanel />
    </div>
  );
}
